import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs/internal/Observable";
import { Endpoint } from "../shared/endpoints";
import { environment } from "src/environments/environment";
import { FlowQueryModel } from "../models/flowquerymodel";
import { OrgContextService } from "./org-context.service";

@Injectable({
  providedIn: "root",
})
export class BundleService {
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private orgContextService: OrgContextService
  ) {}

  PostBundle(bundleModel, value) {
    this.orgContextService.applyToPayload(bundleModel);

    const header = new HttpHeaders()
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .set("emailAddress", value);

    // If this is an existing bundle (has RowKey), use versioning endpoint so save/publish creates a version
    if (bundleModel && (bundleModel.RowKey || bundleModel.rowKey)) {
      const logicalBundleId = (bundleModel.RowKey || bundleModel.rowKey).toString();
      const versionRequest = {
        logicalBundleId: logicalBundleId,
        updatedBundle: bundleModel,
      };

      return this.http.post<any>(
        environment.apiBaseUrl + Endpoint.createBundleVersionUrl,
        versionRequest,
        {
          headers: header,
          reportProgress: true,
          observe: "events",
        }
      );
    }

    // New bundle: use original create endpoint
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.saveBundleUrl,
      bundleModel,
      {
        headers: header,
        reportProgress: true,
        observe: "events",
      }
    );
  }

  setRestrictedAccessOptions(payload: any, emailAddress: string) {
    const header = new HttpHeaders()
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .set("emailAddress", emailAddress);

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.setBundleRestrictedAccessOptionsUrl,
      payload,
      { headers: header }
    );
  }

  changeBundleAccess(bundleModel: any, emailAddress: string) {
    const header = new HttpHeaders()
      .set("Accept", "application/json")
      .set("Content-Type", "application/json")
      .set("emailAddress", emailAddress);

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.changeBundleAccessUrl,
      bundleModel,
      { headers: header }
    );
  }

  /** `draft` returns unpublished edits instead of the published version (editors and reviewers only). */
  getBundleById(flowQueryModel: FlowQueryModel, draft = false): Observable<any> {
    const header = new HttpHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    });

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getBundleByIdUrl,
      flowQueryModel,
      {
        headers: header,
        params: draft ? { draft: "true" } : {},
      }
    );
  }

  reportBundle(bundleModel, value) {
    const header = new HttpHeaders();
    header.append("Accept", "application/json");
    header.append("Content-Type", "application/json");
    header.append("emailAddress", value);

    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.inAppropriateUrl,
      bundleModel,
      {
        headers: header,
      }
    );
  }

  deleteBundle(bundleModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.deleteBundleUrl,
      bundleModel,
      {
        headers: header,
      }
    );
  }

  getBundles(userEmail, inputCategory, pageIndex): Observable<any> {
    let header = new HttpHeaders();
    header = header.append("userName", userEmail);
    header = header.append("inputCategory", inputCategory);
    header = header.append("pageIndex", pageIndex);

    return this.http.get<any>(environment.apiBaseUrl + Endpoint.getBundleUrl, {
      headers: header,
    });
  }

  getBundlesByFlowId(flowModel, pageIndex): Observable<any> {
    let header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.getBundleByFlowIdUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }

  getAllBundleLinkClicks(bundleModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.GetAllBundleLinkClicks,
      bundleModel,
      {
        headers: header,
      }
    );
  }
  LikeBundle(flowModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.likeBundleUrl,
      flowModel,
      {
        headers: header,
      }
    );
  }
  changeTransferOwnershipBundle(ownershipModel): Observable<any> {
    const header = new HttpHeaders();
    return this.http.post<any>(
      environment.apiBaseUrl + Endpoint.changeTransferOwnershipBundle,
      ownershipModel,
      {
        headers: header,
        reportProgress: true, // ✅ Enables progress tracking
        observe: "events", // ✅ Allows listening to upload progress
      }
    );
  }
}
